import { useState } from 'react'
import { Check, Copy, ExternalLink, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  useCreateProjectShareLink,
  useDeleteProjectShareLink,
  useProjectShareLinks,
  useUpdateProjectShareLink,
  type ProjectShareLink,
} from '@/api/hooks'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { LoadingButton } from '@/components/loading-button'
import { getApiErrorMessage } from '@/lib/api-error'

const PORTAL_BASE_URL =
  import.meta.env.VITE_PORTAL_URL || 'http://localhost:5173'

function portalUrl(token: string): string {
  return `${PORTAL_BASE_URL}/p/${token}`
}

export function ProjectShareLinksPanel({
  projectId,
  embedded = false,
}: {
  projectId: string
  /** Inside a dialog that carries its own title: no heading or card. */
  embedded?: boolean
}) {
  const { data: links = [], isLoading } = useProjectShareLinks(projectId)
  const createLink = useCreateProjectShareLink(projectId)
  const updateLink = useUpdateProjectShareLink(projectId)
  const deleteLink = useDeleteProjectShareLink(projectId)

  const handleCreate = () => {
    createLink.mutate(
      { label: null },
      {
        onSuccess: (link) => {
          if (!link) return
          navigator.clipboard.writeText(portalUrl(link.token))
          toast.success('Tracker link created — copied to clipboard')
        },
        onError: (err) =>
          toast.error(
            `Could not create link: ${getApiErrorMessage(err, 'Unknown error')}`,
          ),
      },
    )
  }

  const linkList = (
    <>
      {isLoading ? (
        <p className="text-muted-foreground text-xs">Loading…</p>
      ) : links.length === 0 ? (
        <p className="text-muted-foreground text-xs leading-relaxed">
          No tracker link yet. Click <span className="font-medium">New link</span> to create
          a shareable URL the customer can open without signing in.
        </p>
      ) : (
        links.map((link) => (
          <ShareLinkRow
            key={link.uuid}
            link={link}
            onToggle={(active) =>
              updateLink.mutate({ linkUuid: link.uuid, body: { is_active: active } })
            }
            onDelete={() => deleteLink.mutate(link.uuid)}
            isDeleting={deleteLink.isPending && deleteLink.variables === link.uuid}
          />
        ))
      )}
    </>
  )

  return (
    <div>
      <div className={embedded ? 'mb-2 flex justify-end' : 'mb-3 flex items-center justify-between'}>
        {!embedded && (
          <h3 className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Tracker Portal
          </h3>
        )}
        <LoadingButton
          size="xs"
          variant="outline"
          onClick={handleCreate}
          loading={createLink.isPending}
          loadingText="Creating..."
        >
          <Plus />
          New link
        </LoadingButton>
      </div>
      {embedded ? (
        <div className="space-y-2">{linkList}</div>
      ) : (
        <Card>
          <CardContent className="space-y-2 pt-4 pb-4">{linkList}</CardContent>
        </Card>
      )}
    </div>
  )
}

function ShareLinkRow({
  link,
  onToggle,
  onDelete,
  isDeleting,
}: {
  link: ProjectShareLink
  onToggle: (active: boolean) => void
  onDelete: () => void
  isDeleting: boolean
}) {
  const [copied, setCopied] = useState(false)
  const url = portalUrl(link.token)

  const handleCopy = () => {
    navigator.clipboard.writeText(url)
    setCopied(true)
    toast.success('Copied to clipboard')
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className="min-w-0 flex-1 justify-start gap-1.5 px-2 font-mono text-xs"
        title={url}
      >
        {copied ? (
          <Check className="text-primary size-3 shrink-0" />
        ) : (
          <Copy className="size-3 shrink-0" />
        )}
        <span className="truncate">{url}</span>
      </Button>
      {!link.is_active ? (
        <Badge variant="outline" className="shrink-0 text-xs">
          Disabled
        </Badge>
      ) : null}
      <Switch
        checked={link.is_active}
        onCheckedChange={onToggle}
        title={link.is_active ? 'Disable link' : 'Enable link'}
      />
      {/* eslint-disable-next-line no-restricted-syntax -- external URL */}
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center"
        title="Open portal in new tab"
      >
        <ExternalLink className="size-4" />
      </a>
      <LoadingButton
        variant="ghost"
        size="icon-sm"
        loading={isDeleting}
        onClick={onDelete}
        title="Revoke link"
      >
        <Trash2 />
      </LoadingButton>
    </div>
  )
}
